import { useNavigate } from 'react-router-dom';
import type {
  DashboardCard,
  DashboardCardData,
  DashboardListResult,
  DashboardMetricResult,
  DashboardResultRow,
  DashboardSeriesResult,
} from '@catalyst-psa/shared';
import { DASHBOARD_ENTITY_LABELS } from '@catalyst-psa/shared';

import { Icon } from '../../components/ui/Icon.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { TONE_BAR, TONE_TEXT, formatCell, formatMeasure } from './format.js';

interface DashboardCardViewProps {
  card: DashboardCard;
  data: DashboardCardData | undefined;
  isLoading: boolean;
  /** Edit mode shows the drag handle, edit/remove actions and the resize corner. */
  editing: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onDrillDown: (groupKey: string | null) => void;
  onDragStart: (event: React.PointerEvent<HTMLElement>) => void;
  onResizeStart: (event: React.PointerEvent<HTMLElement>) => void;
}

/** One card on the canvas: chrome plus the renderer for its result kind. */
export function DashboardCardView({
  card,
  data,
  isLoading,
  editing,
  onEdit,
  onDuplicate,
  onRemove,
  onDrillDown,
  onDragStart,
  onResizeStart,
}: DashboardCardViewProps): JSX.Element {
  return (
    <section className="flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <header
        className={`flex items-start justify-between gap-2 px-4 py-2.5 ${
          card.type === 'label' ? '' : 'border-b border-slate-200'
        } ${editing ? 'cursor-move select-none' : ''}`}
        onPointerDown={editing ? onDragStart : undefined}
      >
        <div className="min-w-0">
          <h3
            className={`truncate font-semibold text-slate-900 ${
              card.type === 'label' ? 'text-base' : 'text-sm'
            }`}
          >
            {card.title}
          </h3>
          {card.type === 'label' ? null : (
            <p className="truncate text-xs text-slate-500">
              {(DASHBOARD_ENTITY_LABELS as Record<string, string>)[card.entity] ?? card.entity}
            </p>
          )}
        </div>
        {editing ? (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              onPointerDown={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Edit card"
              title="Edit card"
            >
              <Icon name="pencil" className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onDuplicate}
              onPointerDown={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Copy card"
              title="Copy card"
            >
              <Icon name="copy" className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              onPointerDown={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
              aria-label="Remove card"
              title="Remove card"
            >
              <Icon name="trash" className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </header>

      {card.type === 'label' && !(card.config.body ?? '').trim() ? null : (
        <div className={`min-h-0 flex-1 overflow-auto ${card.type === 'label' ? 'px-4 pb-3' : 'p-4'}`}>
          <CardBody card={card} data={data} isLoading={isLoading} onDrillDown={onDrillDown} />
        </div>
      )}

      {editing ? (
        <button
          type="button"
          onPointerDown={onResizeStart}
          aria-label="Resize card"
          title="Drag to resize"
          className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize rounded-br-xl border-b-2 border-r-2 border-slate-300 hover:border-brand-500"
        />
      ) : null}
    </section>
  );
}

function CardBody({
  card,
  data,
  isLoading,
  onDrillDown,
}: {
  card: DashboardCard;
  data: DashboardCardData | undefined;
  isLoading: boolean;
  onDrillDown: (groupKey: string | null) => void;
}): JSX.Element {
  // Label cards render entirely from their saved config — no query, no data.
  if (card.type === 'label') {
    return card.config.body ? (
      <p className="whitespace-pre-wrap text-sm text-slate-600">{card.config.body}</p>
    ) : (
      <></>
    );
  }
  if (isLoading && !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!data) {
    return <p className="text-sm text-slate-500">No data yet.</p>;
  }
  if (data.error) {
    return <p className="text-sm text-amber-700">{data.error}</p>;
  }
  if (!data.result) {
    return <p className="text-sm text-slate-500">No data.</p>;
  }

  switch (data.result.kind) {
    case 'metric':
      return <MetricBody card={card} result={data.result} onDrillDown={onDrillDown} />;
    case 'list':
      return <ListBody result={data.result} />;
    default:
      return (
        <SeriesBody card={card} result={data.result} onDrillDown={onDrillDown} />
      );
  }
}

function MetricBody({
  card,
  result,
  onDrillDown,
}: {
  card: DashboardCard;
  result: DashboardMetricResult;
  onDrillDown: (groupKey: string | null) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onDrillDown(null)}
      className="flex h-full w-full flex-col items-start justify-center gap-1 rounded text-left hover:bg-slate-50"
      title="Show the records behind this number"
    >
      <span className={`text-4xl font-semibold tabular-nums ${TONE_TEXT[result.tone]}`}>
        {formatMeasure(result.value, result.type)}
      </span>
      {card.config.caption ? (
        <span className="text-xs text-slate-500">{card.config.caption}</span>
      ) : null}
    </button>
  );
}

function ListBody({ result }: { result: DashboardListResult }): JSX.Element {
  const navigate = useNavigate();

  const open = (row: DashboardResultRow, event: React.MouseEvent): void => {
    if (!row.link) return;
    if (event.ctrlKey || event.metaKey) {
      window.open(row.link, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(row.link);
  };

  if (result.rows.length === 0) {
    return <p className="text-sm text-slate-500">Nothing matches this card.</p>;
  }

  return (
    <div className="-mx-1">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-slate-500">
          <tr>
            {result.columns.map((column) => (
              <th key={column.key} className="px-1 pb-2 font-medium">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {result.rows.map((row) => (
            <tr
              key={row.id}
              className={row.link ? 'cursor-pointer hover:bg-slate-50' : undefined}
              onClick={(event) => open(row, event)}
            >
              {result.columns.map((column) => (
                <td key={column.key} className="px-1 py-1.5 text-slate-700">
                  {formatCell(row.cells[column.key] ?? null, column.type)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {result.total > result.rows.length ? (
        <p className="px-1 pt-2 text-xs text-slate-500">
          Showing {result.rows.length} of {result.total}.
        </p>
      ) : null}
    </div>
  );
}

function SeriesBody({
  card,
  result,
  onDrillDown,
}: {
  card: DashboardCard;
  result: DashboardSeriesResult;
  onDrillDown: (groupKey: string | null) => void;
}): JSX.Element {
  if (result.points.length === 0) {
    return <p className="text-sm text-slate-500">Nothing matches this card.</p>;
  }
  return card.type === 'line' ? (
    <LineChart result={result} onDrillDown={onDrillDown} />
  ) : (
    <BarChart result={result} onDrillDown={onDrillDown} />
  );
}

function BarChart({
  result,
  onDrillDown,
}: {
  result: DashboardSeriesResult;
  onDrillDown: (groupKey: string) => void;
}): JSX.Element {
  const max = result.points.reduce((peak, point) => Math.max(peak, Number(point.value)), 0);
  return (
    <div className="space-y-2">
      {result.points.map((point) => {
        const value = Number(point.value);
        const width = max > 0 ? Math.max((value / max) * 100, 2) : 0;
        return (
          <button
            key={point.key}
            type="button"
            onClick={() => onDrillDown(point.key)}
            className="block w-full rounded px-1 py-0.5 text-left hover:bg-slate-50"
            title="Show these records"
          >
            <span className="flex items-baseline justify-between gap-2 text-xs">
              <span className="truncate text-slate-700">{point.label}</span>
              <span className="shrink-0 tabular-nums font-medium text-slate-900">
                {formatMeasure(point.value, result.type)}
              </span>
            </span>
            <span className="mt-1 block h-2 w-full rounded-full bg-slate-100">
              <span
                className={`block h-2 rounded-full ${TONE_BAR.neutral}`}
                style={{ width: `${width}%` }}
              />
            </span>
          </button>
        );
      })}
      {result.truncated ? (
        <p className="pt-1 text-xs text-slate-500">Showing the largest groups only.</p>
      ) : null}
    </div>
  );
}

/** Minimal inline SVG trend line — no charting dependency. */
function LineChart({
  result,
  onDrillDown,
}: {
  result: DashboardSeriesResult;
  onDrillDown: (groupKey: string) => void;
}): JSX.Element {
  const values = result.points.map((point) => Number(point.value));
  const max = Math.max(...values, 1);
  const width = 100;
  const height = 40;
  const step = result.points.length > 1 ? width / (result.points.length - 1) : 0;
  const coords = values.map((value, index) => ({
    x: result.points.length > 1 ? index * step : width / 2,
    y: height - (value / max) * (height - 4) - 2,
  }));
  const path = coords.map((point) => `${point.x},${point.y}`).join(' ');

  return (
    <div className="flex h-full flex-col">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="h-24 w-full"
        role="img"
        aria-label="Trend"
      >
        <polyline
          points={path}
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
          className="text-brand-500"
        />
      </svg>
      <div className="mt-2 grid grid-flow-col auto-cols-fr gap-1">
        {result.points.map((point) => (
          <button
            key={point.key}
            type="button"
            onClick={() => onDrillDown(point.key)}
            className="rounded px-0.5 py-1 text-center hover:bg-slate-50"
            title={`${point.label}: ${formatMeasure(point.value, result.type)}`}
          >
            <span className="block truncate text-[10px] text-slate-500">
              {point.label.slice(0, 3)}
            </span>
            <span className="block truncate text-xs font-medium tabular-nums text-slate-900">
              {formatMeasure(point.value, result.type)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
